// ProfileInsights.jsx (com filtro: All / Temperamento / Carácter)
import React, { useContext, useMemo, useState } from "react";
import styles from "./ProfileInsights.module.css";
import { UserContext } from "../../context/UserContext";
import { updateUserFirebase } from "../../services/firebaseService";

export default function ProfileInsights() {
  const { userData, setUserData } = useContext(UserContext);

  // hooks (sempre no topo)
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(userData?.insightsSavedAt || null);
  const [copied, setCopied] = useState(false);

  // FILTER STATE: 'all' | 'temperament' | 'character'
  const [filterMode, setFilterMode] = useState("all");
  // when character mode, which character to show (label must match computeInsights.characterLabels)
  const [selectedCharacter, setSelectedCharacter] = useState(null);

  // safe fallback para executar hooks consistentemente
  const traitsSafe =
    userData?.personalityTraits && userData.personalityTraits.length === 6
      ? userData.personalityTraits
      : [0, 0, 0, 0, 0, 0];

  // insights computed unconditionally (hook)
  const insights = useMemo(() => computeInsights(traitsSafe), [traitsSafe]);

  // initialize selectedCharacter when insights available
  React.useEffect(() => {
    if (insights.characterLabels && !selectedCharacter) {
      setSelectedCharacter(insights.characterLabels[0]);
    }
  }, [insights.characterLabels, selectedCharacter]);

  if (!userData) return <div className={styles.loading}>Carregando...</div>;

  const temperamentLabels = insights.temperamentLabels; // ["Emocionalidade",...]
  const characterLabels = insights.characterLabels; // ["Disciplina",...]

  async function handleSaveInsights() {
    if (!setUserData) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const patch = { insightsSavedAt: now };
      setUserData((prev) => ({ ...(prev || {}), ...patch }));
      await updateUserFirebase(userData?.id, patch);
      setSavedAt(now);
    } catch (err) {
      console.error("Failed to save insights timestamp:", err);
      alert("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  }

  function handleCopySummary() {
    const text = buildPlainTextSummary(
      insights,
      temperamentLabels,
      characterLabels,
      traitsSafe
    );
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  }

  // helpers for filtered views
  function aggregateTemperamentRow(label) {
    // return concatenated texts of the three cells for this temperament
    const cells = insights.integrationMatrix.filter((c) => c.temper === label);
    return cells.map((c) => `— ${c.character}: ${c.text}`).join("\n");
  }

  function cellsForCharacter(label) {
    return insights.integrationMatrix.filter((c) => c.character === label);
  }

  // PercentBar small component
  function PercentBar({ value }) {
    return (
      <div className={styles.percentBar}>
        <div className={styles.percentFill} style={{ width: `${value}%` }} />
      </div>
    );
  }

  // UI
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Insights do Seu Perfil</h1>
          <p className={styles.subtitle}>
            Integração entre seu temperamento (EAS) e seu carácter (habilidades
            treináveis).
          </p>
        </div>

        <div className={styles.headerActions}>
          <button
            className={styles.ghostBtn}
            onClick={handleCopySummary}
            aria-label="Copiar resumo"
          >
            {copied ? "Copiado ✓" : "Copiar resumo"}
          </button>
          <button
            className={styles.primaryBtn}
            onClick={handleSaveInsights}
            disabled={saving}
          >
            {savedAt
              ? `Salvo em ${new Date(savedAt).toLocaleString()}`
              : saving
              ? "Salvando..."
              : "Salvar insights"}
          </button>
        </div>
      </header>

      <main className={styles.main}>
        {/* Archetype card */}
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Seu arquétipo</h2>
          <div className={styles.row}>
            <div className={styles.avatarBox}>
              <div className={styles.avatarEmoji}>
                {insights.archetype.emoji}
              </div>
            </div>
            <div className={styles.col}>
              <div className={styles.archetypeLabel}>
                {insights.archetype.label}
              </div>
              <div className={styles.archetypeDesc}>
                {insights.archetype.description}
              </div>
            </div>
          </div>
        </section>

        {/* Points & resources */}
        <section className={styles.sideBySide}>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Pontos naturais (Temperamento)</h3>
            <ul className={styles.list}>
              {insights.temperamentStrengths.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Recursos treináveis (Carácter)</h3>
            <ul className={styles.list}>
              {insights.characterResources.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Integration section with filter controls */}
        <section className={styles.card}>
          <div className={styles.integrationHeaderRow}>
            <h3 className={styles.cardTitle}>
              Integração — como seu temperamento influencia o desenvolvimento
            </h3>

            <div className={styles.filterControls}>
              <label className={styles.filterLabel}>Mostrar:</label>
              <div role="tablist" className={styles.filterButtons}>
                <button
                  className={`${styles.filterBtn} ${
                    filterMode === "all" ? styles.filterBtnActive : ""
                  }`}
                  onClick={() => setFilterMode("all")}
                >
                  Tudo
                </button>
                <button
                  className={`${styles.filterBtn} ${
                    filterMode === "temperament" ? styles.filterBtnActive : ""
                  }`}
                  onClick={() => setFilterMode("temperament")}
                >
                  Apenas Temperamento
                </button>
                <button
                  className={`${styles.filterBtn} ${
                    filterMode === "character" ? styles.filterBtnActive : ""
                  }`}
                  onClick={() => setFilterMode("character")}
                >
                  Apenas Carácter
                </button>
              </div>

              {filterMode === "character" && (
                <select
                  className={styles.charSelect}
                  value={selectedCharacter || ""}
                  onChange={(e) => setSelectedCharacter(e.target.value)}
                >
                  {characterLabels.map((cl) => (
                    <option key={cl} value={cl}>
                      {cl}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Different views based on filterMode */}
          {filterMode === "all" && (
            <div className={styles.integrationGrid}>
              {/* Top-left empty cell */}
              <div className={styles.integrationHeader}></div>

              {/* Column headers: show character label + percent */}
              {insights.characterCols.map((col) => (
                <div key={col.label} className={styles.integrationHeader}>
                  <div className={styles.headerLabel}>{col.label}</div>
                  <div className={styles.headerPercent}>{col.value}%</div>
                  <PercentBar value={col.value} />
                </div>
              ))}

              {/* Rows: for each temperament show row header with percent, then 3 cells */}
              {insights.temperRows.map((row) => (
                <React.Fragment key={row.label}>
                  <div className={styles.integrationHeader}>
                    <div className={styles.headerLabel}>{row.label}</div>
                    <div className={styles.headerPercent}>{row.value}%</div>
                    <PercentBar value={row.value} />
                  </div>

                  {/* cells for this row (temperament -> each character) */}
                  {insights.integrationMatrix
                    .filter((cell) => cell.temper === row.label)
                    .map((cell, i) => (
                      <div key={i} className={styles.integrationCell}>
                        <div className={styles.integrationCellText}>
                          {cell.text}
                        </div>
                        <div className={styles.cellMeta}>
                          <small>Temperamento: {cell.temperVal}%</small>
                          <small>
                            {" "}
                            / {cell.character}: {cell.charVal}%
                          </small>
                        </div>
                        <div className={styles.cellBars}>
                          <div className={styles.cellBarLabel}>T</div>
                          <div className={styles.cellBar}>
                            <div
                              style={{ width: `${cell.temperVal}%` }}
                              className={styles.cellBarFillT}
                            />
                          </div>
                          <div className={styles.cellBarLabel}>C</div>
                          <div className={styles.cellBar}>
                            <div
                              style={{ width: `${cell.charVal}%` }}
                              className={styles.cellBarFillC}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </React.Fragment>
              ))}
            </div>
          )}

          {filterMode === "temperament" && (
            <div className={styles.temperamentOnly}>
              {insights.temperRows.map((row) => (
                <div key={row.label} className={styles.temperRowCard}>
                  <div className={styles.temperRowHeader}>
                    <div>
                      <strong>{row.label}</strong>
                      <div className={styles.headerPercentSmall}>
                        {row.value}%
                      </div>
                    </div>
                    <PercentBar value={row.value} />
                  </div>
                  <div className={styles.temperAggText}>
                    {aggregateTemperamentRow(row.label)
                      .split("\n")
                      .map((line, idx) => (
                        <p key={idx} className={styles.temperAggLine}>
                          {line}
                        </p>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {filterMode === "character" && selectedCharacter && (
            <div className={styles.characterOnly}>
              <div className={styles.charHeader}>
                <strong>{selectedCharacter}</strong>
                <div className={styles.headerPercentSmall}>
                  {
                    insights.characterCols.find(
                      (c) => c.label === selectedCharacter
                    )?.value
                  }
                  %
                </div>
                <PercentBar
                  value={
                    insights.characterCols.find(
                      (c) => c.label === selectedCharacter
                    )?.value || 0
                  }
                />
              </div>

              <div className={styles.charColumn}>
                {cellsForCharacter(selectedCharacter).map((cell, i) => (
                  <div key={i} className={styles.integrationCell}>
                    <div className={styles.integrationCellText}>
                      <strong>{cell.temper}</strong>: {cell.text}
                    </div>
                    <div className={styles.cellMeta}>
                      <small>Temperamento: {cell.temperVal}%</small>
                      <small>
                        {" "}
                        / {cell.character}: {cell.charVal}%
                      </small>
                    </div>
                    <div className={styles.cellBars}>
                      <div className={styles.cellBarLabel}>T</div>
                      <div className={styles.cellBar}>
                        <div
                          style={{ width: `${cell.temperVal}%` }}
                          className={styles.cellBarFillT}
                        />
                      </div>
                      <div className={styles.cellBarLabel}>C</div>
                      <div className={styles.cellBar}>
                        <div
                          style={{ width: `${cell.charVal}%` }}
                          className={styles.cellBarFillC}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Recommendations and mini-plan */}
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Recomendações práticas</h3>

          <div className={styles.recoGrid}>
            <div className={styles.recoBlock}>
              <strong>Micro-recomendações</strong>
              <ul className={styles.list}>
                {insights.recommendations.slice(0, 4).map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </div>

            <div className={styles.recoBlock}>
              <strong>Mini-plano de 7 dias</strong>
              <ol className={styles.list}>
                {insights.miniPlan.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Pergunta terapêutica da semana</h3>
          <p className={styles.focusQuestion}>{insights.weeklyQuestion}</p>
        </section>

        <section className={styles.cardSmall}>
          <small className={styles.muted}>
            Nota: estes insights são orientativos e não substituem avaliação
            clínica. Se estiver em terapia, compartilhe estes resultados com seu
            profissional.
          </small>
        </section>
      </main>
    </div>
  );
}

/* ------------------------- Helpers (reuse computeInsights & others) ------------------------- */

function computeInsights(traits) {
  // same implementation as previous version (returns integrationMatrix, temperRows, characterCols, etc.)
  const [
    emotionality,
    activity,
    sociability,
    discipline,
    mentalOpenness,
    emotionalOpenness,
  ] = traits.map((v) => Number(v) || 0);

  const classify = (v) => (v > 60 ? "high" : v >= 40 ? "mid" : "low");

  const Ecls = classify(emotionality);
  const Acls = classify(activity);
  const Scls = classify(sociability);
  const DiscCls = classify(discipline);
  const MindCls = classify(mentalOpenness);
  const EmoCls = classify(emotionalOpenness);

  const archetype = pickArchetype(Ecls, Acls, Scls, {
    emotionality,
    activity,
    sociability,
  });

  const temperamentStrengths = [];
  if (Acls === "high")
    temperamentStrengths.push(
      "Tende a iniciar ações com energia e propensão para movimento."
    );
  if (Acls === "mid")
    temperamentStrengths.push("Combina ação com reflexão — flexível em ritmo.");
  if (Acls === "low")
    temperamentStrengths.push(
      "Economiza energia e tende a planejar antes de agir."
    );

  if (Ecls === "high")
    temperamentStrengths.push(
      "Maior sensibilidade emocional — bom para empatia e expressão."
    );
  if (Ecls === "mid")
    temperamentStrengths.push(
      "Emoções moderadas: equilíbrio entre reatividade e calma."
    );
  if (Ecls === "low")
    temperamentStrengths.push(
      "Estabilidade emocional — menos vulnerável a oscilações momentâneas."
    );

  if (Scls === "high")
    temperamentStrengths.push(
      "Busca conexão social — facilita apoio e aprendizagem em grupo."
    );
  if (Scls === "mid")
    temperamentStrengths.push(
      "Confortável tanto com interação quanto com atividades solitárias."
    );
  if (Scls === "low")
    temperamentStrengths.push(
      "Prefere trabalho individual e ambientes mais silenciosos — bom para concentração profunda."
    );

  const characterResources = [];
  if (DiscCls === "low") {
    characterResources.push(
      "Trabalhar pequenas rotinas de 2–5 minutos para ganhar previsibilidade."
    );
  } else if (DiscCls === "mid") {
    characterResources.push(
      "Estabilizar rituais e usar microplanejamento em momentos de demanda."
    );
  } else {
    characterResources.push(
      "Aprimorar priorização e delegação para evitar rigidez excessiva."
    );
  }

  if (MindCls === "low") {
    characterResources.push(
      "Exercícios simples de perspectiva (3 alternativas) e desfusão cognitiva."
    );
  } else if (MindCls === "mid") {
    characterResources.push(
      "Práticas para aumentar a flexibilidade de pensamento diante de problemas novos."
    );
  } else {
    characterResources.push(
      "Aproveitar sua abertura mental para testar abordagens variadas e documentar aprendizados."
    );
  }

  if (EmoCls === "low") {
    characterResources.push(
      "Exposição emocional gradual e exercícios de aceitação de sensações."
    );
  } else if (EmoCls === "mid") {
    characterResources.push(
      "Expandir tolerância emocional com práticas curtas de presença."
    );
  } else {
    characterResources.push(
      "Usar sua abertura emocional para práticas de vulnerabilidade selecionada e ação baseada em valores."
    );
  }

  // matrix 3x3
  const temperamentLabels = ["Emocionalidade", "Atividade", "Sociabilidade"];
  const characterLabels = [
    "Disciplina",
    "Abertura Mental",
    "Abertura Emocional",
  ];

  const temperValues = {
    Emocionalidade: emotionality,
    Atividade: activity,
    Sociabilidade: sociability,
  };

  const charValues = {
    Disciplina: discipline,
    "Abertura Mental": mentalOpenness,
    "Abertura Emocional": emotionalOpenness,
  };

  const integrationMatrix = [];
  for (const t of temperamentLabels) {
    for (const c of characterLabels) {
      const text = interactionText(t, temperValues[t], c, charValues[c]);
      integrationMatrix.push({
        temper: t,
        character: c,
        text,
        temperVal: Math.round(temperValues[t]),
        charVal: Math.round(charValues[c]),
      });
    }
  }

  const characterCols = characterLabels.map((label) => ({
    label,
    value: Math.round(charValues[label]),
  }));
  const temperRows = temperamentLabels.map((label) => ({
    label,
    value: Math.round(temperValues[label]),
  }));

  const recommendations = generateRecommendations({
    emotionality,
    activity,
    sociability,
    discipline,
    mentalOpenness,
    emotionalOpenness,
  });

  const charValuesArr = [
    { key: "discipline", label: "Disciplina", value: discipline },
    { key: "mind", label: "Abertura Mental", value: mentalOpenness },
    { key: "emotion", label: "Abertura Emocional", value: emotionalOpenness },
  ];
  charValuesArr.sort((a, b) => a.value - b.value);
  const miniPlan = make7DayPlan(charValuesArr[0].key);
  const weeklyQuestion = makeWeeklyQuestion(
    charValuesArr[0].key,
    archetype.label
  );

  return {
    archetype,
    temperamentStrengths,
    characterResources,
    integrationMatrix,
    temperamentLabels,
    characterLabels,
    characterCols,
    temperRows,
    recommendations,
    miniPlan,
    weeklyQuestion,
  };
}

/* ------------------------- interactionText and other helpers same as before ------------------------- */

/* (reuse same interactionText, pickArchetype, generateRecommendations, make7DayPlan, makeWeeklyQuestion, buildPlainTextSummary)
   NOTE: functions use "Disciplina" label where appropriate via computeInsights mapping.
   For brevity in this snippet, reuse those functions from your current file unchanged.
*/

// Interaction text generator: produces practical text that explains how one temp trait impacts one char trait
function interactionText(temper, temperVal, character, charVal) {
  // temperVal and charVal: 0..100
  const tHigh = temperVal > 65;
  const tMid = temperVal >= 40 && temperVal <= 65;
  const tLow = temperVal < 40;

  const cHigh = charVal > 65;
  const cMid = charVal >= 40 && charVal <= 65;
  const cLow = charVal < 40;

  // Use templates for each pairing
  // Emocionalidade -> Organização / Mental / Emocional
  if (temper === "Emocionalidade" && character === "Disciplina") {
    if (tHigh && cLow)
      return "Emoções intensas podem gerar ciclos de ação impulsiva que prejudicam a disciplina. Micro-rotinas de 2 minutos ajudam a aindaar impulso e criar previsibilidade.";
    if (tHigh && cMid)
      return "Sua sensibilidade pode ser aliada: reconheça momentos emocionais e use um checkpoint simples (ex.: 2 minutos) antes de decidir ações importantes.";
    if (tLow && cLow)
      return "Baixa reatividade emocional e pouca disciplina podem levar a procrastinação silenciosa — defina sinais externos (alarme) para iniciar tarefas.";
    if (tLow && cHigh)
      return "Estabilidade emocional combinada com boa disciplina favorece execução calma e consistente.";
    return "Emoções influenciam a clareza de prioridades; observar isso permite ajustar rotinas conforme o estado interno.";
  }

  if (temper === "Emocionalidade" && character === "Abertura Mental") {
    if (tHigh && cLow)
      return "Alto impacto emocional com pouca abertura mental tende a reforçar interpretações únicas dos eventos; perguntas de curiosidade (3 alternativas) ajudam a diluir crenças automáticas.";
    if (tHigh && cHigh)
      return "Sentir intensamente e ter mente aberta é ótimo: use reflexão guiada para transformar emoção em novas perspectivas.";
    if (tLow && cLow)
      return "Pouca emoção e pouca abertura pode levar a apatia cognitiva; experimente pequenos exercícios criativos para ativar outra leitura da situação.";
    return "Emoções afetam a forma como interpretamos informação; cultivar curiosidade beneficia a regulação emocional.";
  }

  if (temper === "Emocionalidade" && character === "Abertura Emocional") {
    if (tHigh && cLow)
      return "Quando emoções são intensas e a abertura é baixa, é comum evitar sentimentos. Práticas graduais de aceitação (1–2 min) são úteis.";
    if (tHigh && cHigh)
      return "Alta sensibilidade com abertura emocional permite explorar experiências internas com apoio — foque em ações valiosas mesmo em presença de desconforto.";
    if (tLow && cLow)
      return "Pouca emoção e pouca abertura podem indicar esquiva: experimentar sentir pequenas emoções escolhidas pode ampliar repertório emocional.";
    return "Abertura emocional modula como a intensidade emocional se traduz em ação. Treinos curtos costumam ter bom custo/benefício.";
  }

  // Atividade -> Organização / Mental / Emocional
  if (temper === "Atividade" && character === "Disciplina") {
    if (tHigh && cLow)
      return "Energia alta com pouca disciplina tende a gerar início de muitas tarefas e pouca conclusão. Use timers curtos e checkpoints para transformar impulso em resultados.";
    if (tHigh && cHigh)
      return "Alta energia + boa disciplina é poderosa: planeje blocos de ação e reserve tempo de recuperação.";
    if (tLow && cLow)
      return "Baixa energia e pouca disciplina dificultam iniciar tarefas; comece com microtarefas de 5 minutos e celebre pequenas conclusões.";
    return "A combinação entre energia e disciplina define velocidade e sustentabilidade das ações — pequenos ajustes na rotina geram impacto grande.";
  }

  if (temper === "Atividade" && character === "Abertura Mental") {
    if (tHigh && cLow)
      return "Atividade elevada com mente fechada pode resultar em ações repetitivas sem testar alternativas; proponha experimentos curtos para validar novas abordagens.";
    if (tHigh && cHigh)
      return "Energia e mente aberta favorecem exploração ágil: faça pequenos testes e registre aprendizados.";
    if (tLow && cLow)
      return "Baixa energia e pouca abertura tornam a exploração de novas ideias mais difícil — comece por anotar uma alternativa por dia.";
    return "A energia disponível influencia a disposição para experimentar novas ideias; alinhe experimentos ao seu nível de energia.";
  }

  if (temper === "Atividade" && character === "Abertura Emocional") {
    if (tHigh && cLow)
      return "Muita ação com pouca abertura emocional pode levar a evitar sentir — considere pausas curtas para checar sensações antes de agir.";
    if (tHigh && cHigh)
      return "Alta energia e abertura emocional permitem agir mesmo com emoções presentes — canalize impulsos para ações valiosas.";
    if (tLow && cLow)
      return "Baixa energia e pouca abertura podem manter a evitação; inicie com exercícios breves de presença antes de agir.";
    return "Energia e abertura emocional juntas determinam se você age apesar do desconforto ou age para evitá-lo.";
  }

  // Sociabilidade -> Organização / Mental / Emocional
  if (temper === "Sociabilidade" && character === "Disciplina") {
    if (tHigh && cLow)
      return "Alto desejo por conexão com baixa disciplina pode favorecer priorizar relações sobre tarefas; usar acordos sociais (com alguém) como compromisso aumenta adesão.";
    if (tHigh && cHigh)
      return "Sociabilidade e disciplina permitem coordenar equipes e manter resultados pelo suporte social.";
    if (tLow && cLow)
      return "Preferência por trabalho solo somada à pouca disciplina pode gerar isolamento produtivo ou desorganização — definir rituais de revisão ajuda.";
    return "Relações influenciam rotinas: incorporar suporte social em checklists aumenta consistência.";
  }

  if (temper === "Sociabilidade" && character === "Abertura Mental") {
    if (tHigh && cLow)
      return "Ser sociável mas pouco aberto mentalmente pode cristalizar crenças de grupo; exponha-se a ideias diversas em conversa estruturada (curiosidade ativa).";
    if (tHigh && cHigh)
      return "Sociabilidade e mente aberta criam terreno fértil para aprender com outros — busque discussões com diversidade de opiniões.";
    if (tLow && cLow)
      return "Pessoas mais reservadas com mente fechada podem perder feedback valioso — experimente compartilhar uma hipótese e pedir 2 interpretações.";
    return "O modo como você se relaciona com os outros altera as oportunidades de ampliar perspectivas.";
  }

  if (temper === "Sociabilidade" && character === "Abertura Emocional") {
    if (tHigh && cLow)
      return "Gosta de estar com pessoas mas evita aprofundar emoções — tente ensaiar partilhas curtas (30–60s) com alguém de confiança.";
    if (tHigh && cHigh)
      return "Sociabilidade com abertura emocional favorece construir vínculos autênticos — pratique vulnerabilidade gradual.";
    if (tLow && cLow)
      return "Preferência por solidão e baixa abertura emocional sugere praticar autorreflexão antes de compartilhar — comece escrevendo sobre emoções.";
    return "Relações e abertura emocional juntas moldam a coragem para se expor; pequenas partilhas são um bom começo.";
  }

  // generic fallback
  return "A interação entre seu temperamento e esta habilidade influencia tendências comportamentais. Pequenos testes e registro ajudam a entender o que funciona para você.";
}

/* ---------------- other helpers (copied/adapted from previous version) ---------------- */

function pickArchetype(Ecls, Acls, Scls, raw) {
  const e = Ecls,
    a = Acls,
    s = Scls;
  const { emotionality = 50, activity = 50, sociability = 50 } = raw;

  if (e === "high" && a === "low" && s === "low") {
    return {
      label: "Guardião",
      emoji: "🛡️",
      description:
        "Sensível, cuidadoso e observador — valoriza segurança e previsibilidade.",
    };
  }
  if (e === "low" && a === "high" && s === "high") {
    return {
      label: "Explorador",
      emoji: "🧭",
      description:
        "Energético, sociável e curioso — bom para iniciar projetos e buscar novas experiências.",
    };
  }
  if (e === "low" && a === "low" && s === "high") {
    return {
      label: "Analista",
      emoji: "🔎",
      description:
        "Calmo e focado, prefere coletar informações e trabalhar com detalhes.",
    };
  }
  if (e === "high" && a === "low" && s === "high") {
    return {
      label: "Artista",
      emoji: "🎨",
      description:
        "Expressivo e empático — conecta emoções e relações com criatividade.",
    };
  }
  if (e === "low" && a === "high" && s === "low") {
    return {
      label: "Construtor",
      emoji: "🔧",
      description:
        "Prático e orientado à ação — prefere resultados concretos e eficiência.",
    };
  }
  if (e === "mid" && a === "mid" && s === "mid") {
    return {
      label: "Navegador",
      emoji: "🧭",
      description: "Equilibrado; adapta-se bem a demandas variadas.",
    };
  }
  if (e === "high" && a === "high" && s === "mid") {
    return {
      label: "Vigilante",
      emoji: "👀",
      description:
        "Enérgico e sensível a sinais de risco — atento e responsivo.",
    };
  }
  const maxRaw = Math.max(emotionality, activity, sociability);
  if (maxRaw === emotionality)
    return {
      label: "Sensível",
      emoji: "💗",
      description:
        "Você processa emoções de forma intensa — isso é fonte de empatia e alerta.",
    };
  if (maxRaw === activity)
    return {
      label: "Dinâmico",
      emoji: "⚡",
      description:
        "Você tem muita energia para agir — útil para mobilizar mudanças.",
    };
  return {
    label: "Conector",
    emoji: "🤝",
    description: "Você valoriza relações e colaboração.",
  };
}

function generateRecommendations(vals) {
  const recs = [];
  if (vals.discipline < 40) {
    recs.push(
      "Estabeleça uma rotina mínima: 2 minutos de planejamento diário antes de iniciar."
    );
    recs.push(
      "Use timers (Pomodoro 25/5 ou 15/5) para transformar energia em foco."
    );
  } else if (vals.discipline < 70) {
    recs.push(
      "Escolha 1 ritual fixo (ex.: revisar prioridades) e mantenha por 7 dias."
    );
  } else {
    recs.push(
      "Delegue quando possível; foque no que traz maior valor em seu dia."
    );
  }

  if (vals.mentalOpenness < 40) {
    recs.push(
      "Pratique listar 3 explicações diferentes para um problema por 3 dias."
    );
    recs.push(
      "Experimente a técnica: 'E se o contrário também fosse verdade?' por 2 minutos."
    );
  } else {
    recs.push(
      "Registre 1 insight novo por dia durante 5 dias para capitalizar a abertura mental."
    );
  }

  if (vals.emotionalOpenness < 40) {
    recs.push(
      "Exercício de 1 minuto: notar sensações no corpo sem agir sobre elas."
    );
    recs.push(
      "Nomeie a emoção: diga em voz baixa 'Estou sentindo X' quando surgir emoção forte."
    );
  } else {
    recs.push(
      "Partilhe uma dificuldade com alguém de confiança esta semana e observe o efeito."
    );
  }

  if (vals.activity > 65)
    recs.push(
      "Quando a energia subir, indique 2 ações pequenas que você pode realmente concluir em 20 minutos."
    );
  if (vals.sociability > 65)
    recs.push(
      "Inclua uma atividade compartilhada (conversa, caminhada) na sua rotina semanal."
    );

  return recs;
}

function make7DayPlan(targetKey) {
  if (targetKey === "discipline") {
    return [
      "Dia 1 — Escolha UMA pequena rotina (2 minutos) para começar: por exemplo, escrever 3 tarefas do dia.",
      "Dia 2 — Configure um lembrete/sinal físico para iniciar a rotina (alarme, post-it).",
      "Dia 3 — Use timer de 15 minutos para fazer a primeira tarefa prioritária.",
      "Dia 4 — Adapte: reduza a rotina se foi difícil ou aumente se foi fácil.",
      "Dia 5 — Compartilhe sua rotina com alguém (curto relato) — accountability aumenta adesão.",
      "Dia 6 — Reflita: o que funcionou? o que atrapalhou? anote 1 ajuste.",
      "Dia 7 — Consolide: mantenha a rotina e planeje próxima semana (mesma lógica).",
    ];
  }
  if (targetKey === "mind") {
    return [
      "Dia 1 — Registre 1 pensamento que te prendia e escreva 2 alternativas para ele.",
      "Dia 2 — Faça um exercício de perspectiva: imagine o mesmo problema visto por outra pessoa.",
      "Dia 3 — Submeta uma hipótese oposta ao seu pensamento dominante por 3 minutos.",
      "Dia 4 — Pratique desfusão: repita um pensamento em voz alta com tom de narrador.",
      "Dia 5 — Liste 3 soluções possíveis, incluindo as menos óbvias.",
      "Dia 6 — Teste uma dessas soluções em pequena escala (experimento de 1 dia).",
      "Dia 7 — Reflita e registre o que mudou na sua visão.",
    ];
  }
  return [
    "Dia 1 — Prática de 2 minutos: notar a respiração e sensações no corpo.",
    "Dia 2 — Nomear emoções: quando surgir algo intenso, diga 'Estou sentindo X'.",
    "Dia 3 — Exposição curta: aceite uma emoção desconfortável por 1 minuto sem tentar mudá-la.",
    "Dia 4 — Validação: reconheça que a emoção tem propósito (mesmo que desconfortável).",
    "Dia 5 — Ação alinhada a valores: escolha uma ação pequena apesar do desconforto.",
    "Dia 6 — Compartilhe com alguém de confiança algo que sentiu esta semana.",
    "Dia 7 — Reflita sobre progresso e planeje pequena prática para seguir.",
  ];
}

function makeWeeklyQuestion(targetKey, archetypeLabel) {
  if (targetKey === "discipline") {
    return `Questão da semana (${archetypeLabel}): Qual pequena rotina de 2 minutos, se repetida, te faria sentir mais no controle das suas tarefas?`;
  }
  if (targetKey === "mind") {
    return `Questão da semana (${archetypeLabel}): Que pensamento você pode olhar hoje com curiosidade (em vez de convicção)? Liste 1 alternativa.`;
  }
  return `Questão da semana (${archetypeLabel}): Que sentimento você evita e que pequeno passo você admite hoje para senti-lo com segurança?`;
}

function buildPlainTextSummary(insights, tempLabels, charLabels, traits) {
  const lines = [];
  lines.push(
    `Arquétipo: ${insights.archetype.label} — ${insights.archetype.description}`
  );
  lines.push("");
  lines.push("Temperamento (percentuais):");
  lines.push(
    `${tempLabels[0]}: ${traits[0]}%  |  ${tempLabels[1]}: ${traits[1]}%  |  ${tempLabels[2]}: ${traits[2]}%`
  );
  lines.push("");
  lines.push("Carácter (percentuais):");
  lines.push(
    `${charLabels[0]}: ${traits[3]}%  |  ${charLabels[1]}: ${traits[4]}%  |  ${charLabels[2]}: ${traits[5]}%`
  );
  lines.push("");
  lines.push("Pontos naturais:");
  insights.temperamentStrengths.forEach((s) => lines.push(`- ${s}`));
  lines.push("");
  lines.push("Recursos treináveis:");
  insights.characterResources.forEach((r) => lines.push(`- ${r}`));
  lines.push("");
  lines.push("Integração (resumo):");
  // include a compact summary of the integration matrix
  insights.integrationMatrix.forEach((cell) => {
    lines.push(`- ${cell.temper} → ${cell.character}: ${cell.text}`);
  });
  lines.push("");
  lines.push("Mini-plano 7 dias:");
  insights.miniPlan.forEach((d, i) => lines.push(`${i + 1}. ${d}`));
  lines.push("");
  lines.push("Pergunta da semana:");
  lines.push(insights.weeklyQuestion);
  return lines.join("\n");
}
